/**
 * Convert plain-text letter content to HTML for the rich text editor.
 * Handles: section headers -> <h3>, bullet lines -> <ul><li>, paragraphs -> <p>
 */

function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function plainTextToHtml(text) {
  if (!text) return '';

  const lines = text.split('\n');
  const htmlParts = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    // Strip markdown bold markers — the AI sometimes wraps labels/details in
    // ** or __ which would otherwise render literally in the letter.
    let trimmed = line.trim().replace(/\*\*/g, '').replace(/__(\S.*?\S|\S)__/g, '$1');

    // Check if it's a header line (ALL CAPS ending with colon, or known section headers)
    const isHeader = /^[A-Z\s]{3,}:$/.test(trimmed) ||
      /^(HISTORY|EXAMINATION|INVESTIGATIONS|DIAGNOSIS|CAUSATION|MANAGEMENT|EXERCISE VITAL SIGN|RADIOLOGY REQUEST FORM):?/.test(trimmed);

    // Check if it's a bullet point
    const isBullet = /^[-•]\s/.test(trimmed);

    if (isHeader) {
      if (inList) {
        htmlParts.push('</ul>');
        inList = false;
      }
      htmlParts.push(`<h3>${escapeHtml(trimmed)}</h3>`);
    } else if (isBullet) {
      if (!inList) {
        htmlParts.push('<ul>');
        inList = true;
      }
      htmlParts.push(`<li>${escapeHtml(trimmed.replace(/^[-•]\s*/, ''))}</li>`);
    } else {
      if (inList) {
        htmlParts.push('</ul>');
        inList = false;
      }
      if (trimmed === '') {
        // Preserve paragraph breaks
        htmlParts.push('<p></p>');
      } else {
        htmlParts.push(`<p>${escapeHtml(trimmed)}</p>`);
      }
    }
  }

  if (inList) {
    htmlParts.push('</ul>');
  }

  return htmlParts.join('');
}

/**
 * Convert HTML back to plain text (strip tags).
 * Used to maintain the `content` field alongside `contentHtml`.
 */
export function htmlToPlainText(html) {
  if (!html) return '';

  // Create a temporary element to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Process node tree to plain text with formatting
  function processNode(node) {
    const parts = [];
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        parts.push(child.textContent);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase();
        if (tag === 'h2' || tag === 'h3') {
          parts.push('\n' + child.textContent.trim() + '\n');
        } else if (tag === 'p') {
          const text = processNode(child).join('');
          parts.push(text + '\n');
        } else if (tag === 'ul' || tag === 'ol') {
          for (const li of child.querySelectorAll('li')) {
            parts.push('- ' + li.textContent.trim() + '\n');
          }
        } else if (tag === 'br') {
          parts.push('\n');
        } else if (tag === 'strong' || tag === 'b') {
          parts.push(child.textContent);
        } else if (tag === 'em' || tag === 'i') {
          parts.push(child.textContent);
        } else {
          parts.push(processNode(child).join(''));
        }
      }
    }
    return parts;
  }

  let result = processNode(temp).join('');
  // Clean up excessive newlines
  result = result.replace(/\n{3,}/g, '\n\n').trim();
  return result;
}
